import { Component, OnInit, OnDestroy, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableModule } from 'primeng/table';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { Select } from 'primeng/select';
import { InputText } from 'primeng/inputtext';
import { Tooltip } from 'primeng/tooltip';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { catchError, of } from 'rxjs';
import { ServiceGroup } from '../../models/service-group.model';
import { ServiceGroupService } from '../../services/service-group.service';
import { Employee } from '../../models/employee.model';
import { EmployeeService } from '../../services/employee.service';
import { AuthService } from '../../services/auth.service';
import { PageHeaderSearchActionService } from '../../services/page-header-search-action.service';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

@Component({
  selector: 'app-service-group-management',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    Button,
    Dialog,
    Select,
    InputText,
    Tooltip,
    ConfirmDialog
  ],
  templateUrl: './service-group-management.component.html',
  styleUrl: './service-group-management.component.scss',
  providers: [ConfirmationService]
})
export class ServiceGroupManagementComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);
  serviceGroups: ServiceGroup[] = [];
  filteredServiceGroups: ServiceGroup[] = [];
  selectedServiceGroup: ServiceGroup = this.getEmptyServiceGroup();
  fieldTouched: Record<string, boolean> = {};
  displayDialog = false;
  dialogMode: 'add' | 'edit' = 'add';
  loading = false;

  /** Employees with a work email, used to show recipients as names in the table. */
  employees: Employee[] = [];
  searchText: string = '';
  selectedStatusFilter: string | null = 'active';

  rows: number = 50;
  first: number = 0;
  totalRecords = 0;
  rowsPerPageOptions: number[] = [5, 10, 20, 50];

  statusFilterOptions: { label: string; value: string | null }[] = [
    { label: 'All', value: null },
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' }
  ];

  get totalServiceGroupsCount(): number { return this.serviceGroups.length; }
  get activeServiceGroupsCount(): number { return this.serviceGroups.filter(sg => sg.isActive).length; }
  get inactiveServiceGroupsCount(): number { return this.serviceGroups.filter(sg => !sg.isActive).length; }

  constructor(
    private serviceGroupService: ServiceGroupService,
    private employeeService: EmployeeService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private authService: AuthService,
    public pageHeaderService: PageHeaderSearchActionService
  ) {}

  ngOnInit() {
    this.pageHeaderService.setCanShowFilterToggle(true);
    this.pageHeaderService.setPlaceholder('Search by name, description or notification email...');
    this.pageHeaderService.setPrimaryButtonLabel('Add Service Group');
    this.pageHeaderService.setCanShowExport(true);
    this.pageHeaderService.setExportDisabled(true);
    this.searchText = this.pageHeaderService.currentSearchText;
    this.loadServiceGroups();
    this.loadEmployees();
    this.pageHeaderService.searchText.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(v => {
      this.searchText = v ?? '';
      this.applySearch();
    });
    this.pageHeaderService.primaryActionClick.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.showAddDialog());
    this.pageHeaderService.exportClick.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.exportToExcel());
    this.authService.user$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(user => {
      this.pageHeaderService.setCanShowPrimaryAction(!!user?.isAdmin);
    });
  }

  ngOnDestroy(): void {
    this.pageHeaderService.clearPageConfig();
  }

  loadServiceGroups() {
    this.loading = true;
    this.serviceGroupService.getAllServiceGroups().pipe(
      catchError(error => {
        console.error('Error loading service groups:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load service groups. Please refresh the page or try again later.'
        });
        this.loading = false;
        // Return empty array to prevent complete failure
        return of([] as ServiceGroup[]);
      })
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        // Ensure isActive is set for all service groups (default to true if missing)
        this.serviceGroups = data.map(sg => ({
          ...sg,
          isActive: sg.isActive !== undefined ? sg.isActive : true
        }));
        // Reapply filters to preserve filtered state
        this.applySearch();
        this.loading = false;
      }
    });
  }

  /**
   * Loads employees for the notification recipients picker. Uses getAllEmployees() because the
   * lightweight /names endpoint returns only id + name — it carries no work email.
   */
  loadEmployees() {
    this.employeeService.getAllEmployees().pipe(
      catchError(error => {
        console.error('Error loading employees:', error);
        this.messageService.add({
          severity: 'warn',
          summary: 'Employees unavailable',
          detail: 'Could not load the employee list. Notification recipients cannot be selected.'
        });
        return of([] as Employee[]);
      })
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        this.employees = (data ?? [])
          .filter(e => e.isActive && !!e.email?.trim())
          .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
      }
    });
  }

  /** Renders stored recipient emails as employee names where known, for the table cell. */
  notificationRecipientNames(serviceGroup: ServiceGroup): string {
    const emails = this.splitNotificationEmails(serviceGroup.notificationEmails);
    if (emails.length === 0) {
      return '';
    }
    const byEmail = new Map(
      this.employees.filter(e => e.email).map(e => [e.email!.trim().toLowerCase(), e.name])
    );
    return emails.map(email => byEmail.get(email.toLowerCase()) ?? email).join(', ');
  }

  onSearch() {
    this.applySearch();
  }

  applySearch() {
    let filtered = [...this.serviceGroups];

    // Apply search text filter
    if (this.searchText.trim() !== '') {
      const searchLower = this.searchText.toLowerCase();
      filtered = filtered.filter(sg =>
        sg.name?.toLowerCase().includes(searchLower) ||
        sg.description?.toLowerCase().includes(searchLower) ||
        sg.notificationEmails?.toLowerCase().includes(searchLower)
      );
    }

    // Apply status filter
    if (this.selectedStatusFilter !== null) {
      if (this.selectedStatusFilter === 'active') {
        filtered = filtered.filter(sg => sg.isActive === true);
      } else if (this.selectedStatusFilter === 'inactive') {
        filtered = filtered.filter(sg => sg.isActive === false);
      }
    }

    this.filteredServiceGroups = filtered;
    this.totalRecords = this.filteredServiceGroups.length;
    this.first = 0;
    this.pageHeaderService.setExportDisabled(this.filteredServiceGroups.length === 0);
  }

  clearSearch() {
    this.searchText = '';
    this.pageHeaderService.setSearchText('');
    this.applySearch();
  }

  onStatusFilterChange() {
    this.applySearch();
  }

  hasActiveFilters(): boolean {
    return !!(this.searchText.trim() || (this.selectedStatusFilter != null && this.selectedStatusFilter !== 'active'));
  }

  clearFilters() {
    this.searchText = '';
    this.pageHeaderService.setSearchText('');
    this.selectedStatusFilter = 'active';
    this.applySearch();
  }

  toggleActiveStatus(serviceGroup: ServiceGroup) {
    const activating = !serviceGroup.isActive;
    this.confirmationService.confirm({
      message: `Are you sure you want to ${activating ? 'activate' : 'deactivate'} "${serviceGroup.name}"?`,
      header: activating ? 'Confirm Activate' : 'Confirm Deactivate',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: activating ? 'p-button-success' : 'p-button-danger',
      accept: () => {
        const updatedServiceGroup = { ...serviceGroup, isActive: !serviceGroup.isActive };
        this.serviceGroupService.updateServiceGroup(serviceGroup.id, updatedServiceGroup).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: `Service Group ${updatedServiceGroup.isActive ? 'activated' : 'deactivated'} successfully`
            });
            this.loadServiceGroups();
          },
          error: (error) => {
            console.error('Error updating service group status:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to update service group status'
            });
          }
        });
      }
    });
  }

  showAddDialog() {
    this.selectedServiceGroup = this.getEmptyServiceGroup();
    this.fieldTouched = {};
    this.dialogMode = 'add';
    this.displayDialog = true;
  }

  showEditDialog(serviceGroup: ServiceGroup) {
    this.selectedServiceGroup = { ...serviceGroup };
    this.fieldTouched = {};
    this.dialogMode = 'edit';
    this.displayDialog = true;
  }

  hideDialog() {
    this.displayDialog = false;
    this.selectedServiceGroup = this.getEmptyServiceGroup();
    this.fieldTouched = {};
  }

  resetServiceGroupForm() {
    const isEdit = this.dialogMode === 'edit';
    const id = this.selectedServiceGroup.id;
    // Recipients are not edited on this form, so carry them across a reset rather than blanking
    // them — clearing the form must not clear a service group's contract recipients.
    const notificationEmails = this.selectedServiceGroup.notificationEmails;
    this.selectedServiceGroup = this.getEmptyServiceGroup();
    if (isEdit) {
      this.selectedServiceGroup.id = id;
      this.selectedServiceGroup.notificationEmails = notificationEmails;
    }
    this.fieldTouched = {};
  }

  exportToExcel() {
    if (this.filteredServiceGroups.length === 0) return;
    const headers = ['Name', 'Description', 'Notification Emails', 'Status'];
    const rows = this.filteredServiceGroups.map(sg => [
      sg.name ?? '',
      sg.description ?? '',
      sg.notificationEmails ?? '',
      sg.isActive ? 'Active' : 'Inactive'
    ]);
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Service Groups');
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([wbout], { type: 'application/octet-stream' }), `service-groups_${this.formatDateForFile()}.xlsx`);
  }

  formatDateForFile(): string {
    const d = new Date();
    return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}_${String(d.getHours()).padStart(2, '0')}${String(d.getMinutes()).padStart(2, '0')}`;
  }

  /** Splits a stored semicolon/comma separated recipient string into individual addresses. */
  private splitNotificationEmails(raw?: string): string[] {
    if (!raw?.trim()) {
      return [];
    }
    return raw
      .split(/[;,]/)
      .map(e => e.trim())
      .filter(e => e.length > 0);
  }

  saveServiceGroup() {
    this.fieldTouched['name'] = true;
    if (!this.selectedServiceGroup.name.trim()) {
      return;
    }

    // notificationEmails is deliberately not touched here — it is edited on the Notification
    // Settings page and simply carried through on the object so saving a name or description
    // cannot wipe a service group's contract recipients.

    if (this.dialogMode === 'add') {
      this.serviceGroupService.addServiceGroup(this.selectedServiceGroup).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Service Group added successfully'
          });
          this.loadServiceGroups();
          this.hideDialog();
        },
        error: (error) => {
          console.error('Error adding service group:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to add service group'
          });
        }
      });
    } else {
      this.serviceGroupService.updateServiceGroup(this.selectedServiceGroup.id, this.selectedServiceGroup).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Service Group updated successfully'
          });
          this.loadServiceGroups();
          this.hideDialog();
        },
        error: (error) => {
          console.error('Error updating service group:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update service group'
          });
        }
      });
    }
  }

  private getEmptyServiceGroup(): ServiceGroup {
    return {
      id: '',
      name: '',
      description: '',
      isActive: true,
      notificationEmails: ''
    };
  }
}


